import React, { Component, ReactNode } from 'react';
import * as Sentry from '@sentry/react';
import { Button } from './button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './card';
import { AlertTriangle, RefreshCw, Home, Sparkles } from 'lucide-react';
import i18n from '@/lib/i18n';

interface ErrorBoundaryState {
    hasError: boolean;
    error?: Error;
    errorInfo?: React.ErrorInfo;
}

interface ErrorBoundaryProps {
    children: ReactNode;
    fallback?: ReactNode;
    onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
    resetOnPropsChange?: boolean;
    resetKeys?: Array<string | number>;
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
    private resetTimeoutId: number | null = null;

    constructor(props: ErrorBoundaryProps) {
        super(props);
        this.state = { hasError: false };
    }

    static getDerivedStateFromError(error: Error): ErrorBoundaryState {
        return { hasError: true, error };
    }

    componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
        this.setState({ error, errorInfo });

        // Send to Sentry with additional context
        Sentry.withScope((scope) => {
            scope.setTag('errorBoundary', true);
            scope.setContext('errorInfo', {
                componentStack: errorInfo.componentStack,
                timestamp: new Date().toISOString(),
            });
            scope.setLevel('error');
            Sentry.captureException(error);
        });

        // Log error using existing logger
        if (globalThis.logger) {
            globalThis.logger.error('ErrorBoundary caught an error:', {
                error: error.message,
                stack: error.stack,
                componentStack: errorInfo.componentStack,
                timestamp: new Date().toISOString()
            });
        }

        // Call custom error handler if provided
        if (this.props.onError) {
            this.props.onError(error, errorInfo);
        }
    }

    componentDidUpdate(prevProps: ErrorBoundaryProps) {
        const { resetKeys, resetOnPropsChange } = this.props;
        const { hasError } = this.state;

        if (hasError && prevProps.resetKeys !== resetKeys) {
            if (resetOnPropsChange) {
                this.resetErrorBoundary();
            }
        }
    }

    componentWillUnmount() {
        if (this.resetTimeoutId) {
            clearTimeout(this.resetTimeoutId);
        }
    }

    resetErrorBoundary = () => {
        this.setState({ hasError: false, error: undefined, errorInfo: undefined });
    };

    refreshPage = () => {
        window.location.reload();
    };

    isChunkLoadError = (error?: Error): boolean => {
        if (!error) return false;

        const errorMessage = error.message.toLowerCase();
        return (
            errorMessage.includes('failed to fetch dynamically imported module') ||
            errorMessage.includes('error loading dynamically imported module') ||
            errorMessage.includes('importing a module script failed') ||
            errorMessage.includes('dynamically imported module')
        );
    };

    render() {
        if (this.state.hasError) {
            const isChunkError = this.isChunkLoadError(this.state.error);

            // Special UI for chunk loading errors (new version available)
            // This takes priority over custom fallbacks
            if (isChunkError) {
                return (
                    <div className="min-h-screen flex items-center justify-center p-4">
                        <Card className="w-full max-w-md">
                            <CardHeader className="text-center">
                                <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
                                    <Sparkles className="h-6 w-6 text-green-600" />
                                </div>
                                <CardTitle className="text-xl">{i18n.t('errors.chunkLoadTitle', { ns: 'common' })}</CardTitle>
                                <CardDescription>
                                    {i18n.t('errors.chunkLoadDescription', { ns: 'common' })}
                                </CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <Button
                                    onClick={this.refreshPage}
                                    className="w-full"
                                    variant="default"
                                >
                                    <RefreshCw className="mr-2 h-4 w-4" />
                                    {i18n.t('errors.refreshNow', { ns: 'common' })}
                                </Button>
                            </CardContent>
                        </Card>
                    </div>
                );
            }

            // Use custom fallback if provided (for non-chunk errors)
            if (this.props.fallback) {
                return this.props.fallback;
            }

            // Default error UI for other errors
            return (
                <div className="min-h-screen flex items-center justify-center p-4">
                    <Card className="w-full max-w-md">
                        <CardHeader className="text-center">
                            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
                                <AlertTriangle className="h-6 w-6 text-red-600" />
                            </div>
                            <CardTitle className="text-xl">{i18n.t('errors.somethingWrong', { ns: 'common' })}</CardTitle>
                            <CardDescription>
                                {i18n.t('errors.unexpectedError', { ns: 'common' })}
                            </CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="flex flex-col gap-2">
                                <Button
                                    onClick={this.resetErrorBoundary}
                                    className="w-full"
                                    variant="default"
                                >
                                    <RefreshCw className="mr-2 h-4 w-4" />
                                    {i18n.t('errors.tryAgain', { ns: 'common' })}
                                </Button>
                                <Button
                                    onClick={() => window.location.href = '/my-account'}
                                    variant="outline"
                                    className="w-full"
                                >
                                    <Home className="mr-2 h-4 w-4" />
                                    {i18n.t('errors.goToDashboard', { ns: 'common' })}
                                </Button>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            );
        }

        return this.props.children;
    }
}

export default ErrorBoundary;
